import { useState } from 'react';
import { X, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ETAModal = ({ booking, barber, open, onClose, onAccepted }) => {
  const [eta, setEta] = useState('30');
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!barber) {
      toast.error('Barber info not available');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/accept`, { 
        barber_eta: eta,
        barber_id: barber.id,
        barber_name: barber.full_name
      });
      toast.success(`Booking accepted! Customer notified of ${eta} min ETA`);
      if (onAccepted) onAccepted();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.data?.detail === "Booking already accepted by another barber") {
        toast.error('This booking was already accepted by another barber');
      } else {
        toast.error('Failed to accept booking');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open || !booking) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[var(--text-primary)]">Accept Booking</h3>
          <button onClick={onClose} className="text-[var(--text-secondary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-[var(--bg-primary)] dark:bg-[#0A0A0A] rounded-lg">
            <div className="text-sm text-[var(--text-secondary)] mb-1">Customer</div>
            <div className="font-semibold text-[var(--text-primary)]">{booking.customer_name}</div>
            <div className="text-sm text-[var(--text-secondary)] mt-2">{booking.address}</div>
          </div>

          <div className="p-4 bg-[var(--bg-primary)] dark:bg-[#0A0A0A] rounded-lg">
            <div className="text-sm text-[var(--text-secondary)] mb-1">Service</div>
            <div className="font-semibold text-[var(--text-primary)]">{booking.service_name}</div>
            <div className="text-sm text-[var(--text-secondary)]">{booking.booking_date} at {booking.booking_time}</div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              How long until you arrive?
            </label>
            <Select value={eta} onValueChange={setEta}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="60+">More than 1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#00C167]/10 border border-[#00C167]/20 rounded-lg p-3 text-center">
            <Check className="w-5 h-5 text-[#00C167] inline mr-2" />
            <span className="text-sm font-medium text-[var(--text-primary)]">
              Customer will be notified of your {eta === '60+' ? '60+' : eta} minute ETA
            </span>
          </div>

          <Button
            onClick={handleAccept}
            disabled={loading}
            className="w-full bg-[#00C167] hover:bg-[#009E54] text-white font-semibold h-12"
          >
            {loading ? 'Accepting...' : 'Accept Booking'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ETAModal;